import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Button, Card, Input, useToast } from '@caruso/ui';
import { clientsService } from '@/services/clients.service';
import {
  Plus,
  Search,
  Edit,
  Archive,
  Filter,
  ChevronLeft,
  ChevronRight,
  Upload,
  Download,
  X,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { cn } from '@caruso/ui';

export function ClientsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showArchived, setShowArchived] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<{
    total: number;
    imported: number;
    errors: Array<{ row: number; error: string; data: any }>;
  } | null>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1); // Reset to first page when searching
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['clients', { search, page, archived: showArchived }],
    queryFn: () => {
      console.log('📊 Estado antes da chamada:', { search, page, showArchived });
      return clientsService.getAll({
        search,
        page,
        pageSize: 10,
        archived: showArchived,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      });
    },
  });

  const handleArchive = async (id: string) => {
    try {
      await clientsService.archive(id);
      toast({
        title: 'Cliente arquivado',
        variant: 'success',
      });
      refetch();
    } catch (error) {
      toast({
        title: 'Erro ao arquivar cliente',
        variant: 'destructive',
      });
    }
  };

  const handleUnarchive = async (id: string) => {
    try {
      await clientsService.unarchive(id);
      toast({
        title: 'Cliente desarquivado',
        variant: 'success',
      });
      refetch();
    } catch (error) {
      toast({
        title: 'Erro ao desarquivar cliente',
        variant: 'destructive',
      });
    }
  };

  const importMutation = useMutation({
    mutationFn: (file: File) => clientsService.importFromCsv(file),
    onSuccess: (result) => {
      setImportResult(result);
      refetch();
      if (result.errors.length === 0) {
        toast({
          title: 'Importação concluída!',
          description: `${result.imported} cliente(s) importado(s) com sucesso`,
          variant: 'success',
        });
      } else {
        toast({
          title: 'Importação concluída com erros',
          description: `${result.imported} importados, ${result.errors.length} com erro`,
          variant: 'default',
        });
      }
    },
    onError: () => {
      toast({
        title: 'Erro na importação',
        description: 'Verifique se o arquivo está no formato correto',
        variant: 'destructive',
      });
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  const handleImport = () => {
    if (selectedFile) {
      importMutation.mutate(selectedFile);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await clientsService.downloadTemplate();
      toast({
        title: 'Template baixado!',
        variant: 'success',
      });
    } catch (error) {
      toast({
        title: 'Erro ao baixar template',
        variant: 'destructive',
      });
    }
  };

  const handleCloseModal = () => {
    setShowImportModal(false);
    setSelectedFile(null);
    setImportResult(null);
  };

  const formatCPF = (cpf?: string) => {
    if (!cpf) return '';
    // Remove caracteres não numéricos
    const cleanCPF = cpf.replace(/\D/g, '');
    // Aplica a máscara
    return cleanCPF.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-serif text-brand-gold">Clientes</h1>
          <p className="text-brand-ivory/60 mt-1">
            Gerencie sua carteira de clientes
          </p>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => setShowImportModal(true)} variant="outline">
            <Upload className="w-4 h-4 mr-2" />
            Importar
          </Button>
          <Button onClick={() => navigate('/app/clientes/novo')} variant="premium">
            <Plus className="w-4 h-4 mr-2" />
            Novo Cliente
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-brand-ink/50 border-brand-gold/20">
        <div className="p-4 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-gold/50" />
            <Input
              placeholder="Buscar por nome, email, telefone, documento..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-10 bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
            />
          </div>
          <Button
            variant={showArchived ? 'default' : 'outline'}
            onClick={() => {
              setShowArchived(!showArchived);
              setPage(1);
            }}
          >
            <Archive className="w-4 h-4 mr-2" />
            {showArchived ? 'Mostrando: Arquivados' : 'Mostrando: Ativos'}
          </Button>
        </div>
      </Card>

      {/* Clients List */}
      {isLoading ? (
        <div className="text-center py-12 text-brand-ivory/50">
          Carregando...
        </div>
      ) : (
        <>
          <div className="grid gap-4">
            {data?.data.map((client) => (
              <motion.div
                key={client.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.01 }}
              >
                <Card
                  className="bg-brand-ink/50 border-brand-gold/20 hover:border-brand-gold/40 transition-all cursor-pointer"
                  onClick={() => navigate(`/app/clientes/${client.id}`)}
                >
                  <div className="p-6 flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-serif text-brand-gold">
                        {client.name}
                      </h3>
                      <div className="mt-2 flex flex-wrap gap-4 text-sm text-brand-ivory/60">
                        {client.email && <span>{client.email}</span>}
                        {client.phone && <span>{client.phone}</span>}
                        {client.document && <span>CPF: {formatCPF(client.document)}</span>}
                      </div>
                      {(client.address || client.city) && (
                        <div className="mt-2 text-sm text-brand-ivory/50">
                          {client.address && (
                            <span>
                              {client.address}
                              {client.number && `, ${client.number}`}
                              {client.neighborhood && ` - ${client.neighborhood}`}
                            </span>
                          )}
                          {client.city && client.state && (
                            <span>
                              {client.address && ' - '}
                              {client.city}/{client.state}
                            </span>
                          )}
                        </div>
                      )}
                      {client.tags && client.tags.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {client.tags.map((ct) => (
                            <span
                              key={ct.tagId}
                              className="px-2 py-1 text-xs rounded-full bg-brand-gold/20 text-brand-gold"
                            >
                              {ct.tag?.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/app/clientes/${client.id}`);
                        }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      {client.archived ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUnarchive(client.id);
                          }}
                        >
                          <Archive className="w-4 h-4 text-green-400" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleArchive(client.id);
                          }}
                        >
                          <Archive className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Pagination */}
          {data && data.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="px-4 text-brand-ivory">
                Página {page} de {data.totalPages}
              </span>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setPage(page + 1)}
                disabled={page === data.totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}

          {data?.data.length === 0 && (
            <div className="text-center py-12">
              <p className="text-brand-ivory/50">Nenhum cliente encontrado</p>
              <Button
                onClick={() => navigate('/app/clientes/novo')}
                variant="outline"
                className="mt-4"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar primeiro cliente
              </Button>
            </div>
          )}
        </>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-brand-ink border border-brand-gold/20 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-brand-gold">Importar Clientes</h2>
              <Button variant="ghost" size="icon" onClick={handleCloseModal}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            {!importResult ? (
              <div className="space-y-6">
                <div className="bg-brand-smoke border border-brand-gold/10 rounded-lg p-6">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <Download className="w-5 h-5 text-brand-gold" />
                    Passo 1: Baixar Template
                  </h3>
                  <p className="text-brand-ivory/70 mb-4 text-sm">
                    Baixe o arquivo de exemplo com o formato correto para importação.
                  </p>
                  <Button onClick={handleDownloadTemplate} variant="outline" className="w-full">
                    <Download className="w-4 h-4 mr-2" />
                    Baixar Template CSV
                  </Button>
                </div>

                <div className="bg-brand-smoke border border-brand-gold/10 rounded-lg p-6">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <Upload className="w-5 h-5 text-brand-gold" />
                    Passo 2: Selecionar Arquivo
                  </h3>
                  <p className="text-brand-ivory/70 mb-4 text-sm">
                    Selecione o arquivo CSV preenchido com os dados dos clientes.
                  </p>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="hidden"
                    id="csv-upload"
                  />
                  <label htmlFor="csv-upload" className="block">
                    <div className="border-2 border-dashed border-brand-gold/30 rounded-lg p-8 text-center cursor-pointer hover:border-brand-gold/50 transition-colors">
                      {selectedFile ? (
                        <div>
                          <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                          <p className="text-white font-medium">{selectedFile.name}</p>
                          <p className="text-brand-ivory/60 text-sm mt-1">
                            {(selectedFile.size / 1024).toFixed(2)} KB
                          </p>
                        </div>
                      ) : (
                        <div>
                          <Upload className="w-12 h-12 text-brand-gold/50 mx-auto mb-3" />
                          <p className="text-brand-ivory/70">Clique para selecionar o arquivo CSV</p>
                        </div>
                      )}
                    </div>
                  </label>
                </div>

                <div className="flex gap-3">
                  <Button variant="outline" onClick={handleCloseModal} className="flex-1">
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleImport}
                    disabled={!selectedFile || importMutation.isPending}
                    className="flex-1"
                  >
                    {importMutation.isPending ? 'Importando...' : 'Importar Clientes'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="bg-brand-smoke border border-brand-gold/10 rounded-lg p-6">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    Resultado da Importação
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                    <div className="bg-brand-ink/30 rounded-lg p-4 text-center">
                      <p className="text-brand-ivory/60 text-sm mb-1">Total</p>
                      <p className="text-2xl font-bold text-white">{importResult.total}</p>
                    </div>
                    <div className="bg-green-500/10 rounded-lg p-4 text-center">
                      <p className="text-brand-ivory/60 text-sm mb-1">Importados</p>
                      <p className="text-2xl font-bold text-green-400">{importResult.imported}</p>
                    </div>
                    <div className="bg-red-500/10 rounded-lg p-4 text-center">
                      <p className="text-brand-ivory/60 text-sm mb-1">Erros</p>
                      <p className="text-2xl font-bold text-red-400">{importResult.errors.length}</p>
                    </div>
                  </div>

                  {importResult.errors.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-white font-medium flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-red-400" />
                        Erros Encontrados
                      </h4>
                      <div className="max-h-64 overflow-y-auto space-y-2">
                        {importResult.errors.map((error, index) => (
                          <div key={index} className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                            <p className="text-red-400 font-medium text-sm">Linha {error.row}</p>
                            <p className="text-brand-ivory/70 text-sm">{error.error}</p>
                            <p className="text-brand-ivory/50 text-xs mt-1">
                              {JSON.stringify(error.data)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <Button onClick={handleCloseModal} className="w-full">
                  Fechar
                </Button>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
}